/**
 * ChatsListSelection Hook
 *
 * Manages selection state, infinite scroll, and scroll shadow indicators.
 *
 * Selection: Streams don't re-render child elements when parent assigns change,
 * so we need JS to update the data-selected attribute for CSS styling.
 *
 * Infinite Scroll: The built-in Phoenix.InfiniteScroll hook only has mounted()
 * with no updated(), so when navigating via LiveView (element morphed, not replaced),
 * the scroll listener never gets attached. We handle it here since this hook
 * is on the scroll container.
 *
 * Scroll Shadows: Toggles .has-scroll-above/.has-scroll-below classes for CSS
 * to show gradient shadows indicating more content exists above/below.
 *
 * Usage:
 * <div phx-hook="ChatsListSelection" data-selected-chat-id={@selected_chat_id} class="scroll-shadows">
 *   <div phx-update="stream" phx-viewport-bottom={JS.push(...)}>
 *     <div :for={{dom_id, chat} <- @streams.chats} data-chat-id={chat.id}>
 *       ...
 *     </div>
 *   </div>
 * </div>
 */
export const ChatsListSelection = {
  mounted() {
    this.updateSelectedState()
    this.setupInfiniteScroll()
    this.updateScrollShadows()
  },

  updated() {
    this.updateSelectedState()
    // Re-setup infinite scroll on updates (in case stream container changed)
    this.setupInfiniteScroll()
    // Update shadows after content changes
    requestAnimationFrame(() => this.updateScrollShadows())
  },

  destroyed() {
    this.cleanupInfiniteScroll()
  },

  setupInfiniteScroll() {
    // Clean up any existing listener
    this.cleanupInfiniteScroll()

    // Find the stream container with phx-viewport-bottom
    this.streamContainer = this.el.querySelector('[phx-viewport-bottom]')
    if (!this.streamContainer) {
      return
    }

    // The scroll container is this.el (has overflow-y: auto)
    this.scrollContainer = this.el

    // Throttle settings for infinite scroll (not for shadows)
    const throttleInterval = 500
    let lastCallAt = 0
    let pendingOp = false

    this.onScroll = () => {
      // Always update scroll shadows (cheap operation)
      this.updateScrollShadows()

      // Throttle infinite scroll trigger
      if (pendingOp) {
        return
      }

      const now = Date.now()
      if (now - lastCallAt < throttleInterval) {
        return
      }

      // Check if we should trigger load-more
      const viewportBottomAttr = this.streamContainer.getAttribute(
        'phx-viewport-bottom'
      )
      if (!viewportBottomAttr) {
        return
      }

      const lastChild = this.streamContainer.lastElementChild
      if (!lastChild) {
        return
      }

      // Check if last child is near the bottom of the scroll container
      const containerRect = this.scrollContainer.getBoundingClientRect()
      const lastChildRect = lastChild.getBoundingClientRect()

      // Trigger when bottom of last child is within viewport
      if (lastChildRect.bottom <= containerRect.bottom + 100) {
        lastCallAt = now
        pendingOp = true

        // Parse and execute the JS command
        this.liveSocket.execJS(this.streamContainer, viewportBottomAttr)

        // Reset pending after a short delay
        setTimeout(() => {
          pendingOp = false
        }, throttleInterval)
      }
    }

    this.scrollContainer.addEventListener('scroll', this.onScroll, {
      passive: true
    })

    // Also check immediately (for initial load)
    requestAnimationFrame(() => this.onScroll())
  },

  /**
   * Toggle scroll shadow classes based on scroll position.
   * Classes are set on the parent wrapper (which has the pseudo-elements),
   * not on this.el (the scroll container) because overflow:auto clips pseudo-elements.
   */
  updateScrollShadows() {
    const { scrollTop, scrollHeight, clientHeight } = this.el
    const wrapper = this.el.parentElement
    if (!wrapper) {
      return
    }

    const threshold = 5
    const hasScrollAbove = scrollTop > threshold
    const hasScrollBelow = scrollTop + clientHeight < scrollHeight - threshold

    wrapper.classList.toggle('has-scroll-above', hasScrollAbove)
    wrapper.classList.toggle('has-scroll-below', hasScrollBelow)
  },

  cleanupInfiniteScroll() {
    if (this.onScroll && this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.onScroll)
    }
    this.onScroll = null
    this.streamContainer = null
  },

  updateSelectedState() {
    const selectedId = this.el.dataset.selectedChatId

    // Remove data-selected from all chat items
    for (const el of this.el.querySelectorAll(
      '[data-chat-id][data-selected]'
    )) {
      el.removeAttribute('data-selected')
    }

    // Add data-selected to the currently selected chat
    if (selectedId) {
      const selected = this.el.querySelector(`[data-chat-id="${selectedId}"]`)
      if (selected) {
        selected.setAttribute('data-selected', '')
      }
    }
  }
}
